import * as React from 'react';
import BokunLogo from 'assets/bokun-logo.svg';
import Typography from '@material-ui/core/Typography';
import { Form } from 'react-final-form';
import { TextField } from 'mui-rff';
import * as Yup from 'yup';
import Grid from '@material-ui/core/Grid';
import { IconButton, InputAdornment, SnackbarContent } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { setIn } from 'final-form';
import { useStyles } from './css';
import { ApolloError } from 'apollo-client';
import { BokunTermAgreements } from '../../../components/BokunTermAgreements';

interface DeveloperSignupViewProps {
    onSubmit: (values, form, callback) => void;
    error: ApolloError;
    loading: boolean;
}

export const DeveloperSignupView = ({ onSubmit, error, loading }: DeveloperSignupViewProps) => {
    const classes = useStyles();
    const [showPassword, setShowPassword] = React.useState(false);

    const validate = schema => {
        // eslint-disable-next-line consistent-return
        return async values => {
            if (typeof schema === 'function') {
                schema = schema();
            }

            try {
                await schema.validate(values, { abortEarly: false });
            } catch (e: any) {
                return e.inner.reduce((errors, validationError) => {
                    return setIn(errors, validationError.path, validationError.message);
                }, {});
            }
        };
    };

    return (
        <Container component="main" maxWidth="xs">
            <div className={classes.paper}>
                <img src={BokunLogo} className={classes.logo} alt={'Bokun'} />
                <Typography component="h1" variant="h5">
                    Create a Bókun Partner Account
                </Typography>

                <Form
                    initialValues={{
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        termsApiAgreed: false,
                        termsPartnerAgreed: false,
                    }}
                    validate={validate(
                        Yup.object().shape({
                            firstName: Yup.string().required('First Name is required'),
                            lastName: Yup.string().required('Last Name is required'),
                            email: Yup.string()
                                .email('Email is invalid')
                                .required('Email is required'),
                            password: Yup.string()
                                .required('Password is required')
                                .min(8, 'Password must be at least 8 chars')
                                .max(255),
                            termsApiAgreed: Yup.bool().oneOf(
                                [true],
                                'You must agree to our Bókun API Terms'
                            ),
                            termsPartnerAgreed: Yup.bool().oneOf(
                                [true],
                                'You must agree to our Bókun Partner terms'
                            ),
                        })
                    )}
                    onSubmit={onSubmit}
                >
                    {({ pristine, handleSubmit }) => (
                        <form className={classes.form} onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        type="email"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        autoFocus
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="firstName"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        id="firstName"
                                        label="First Name"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        id="lastName"
                                        label="Last Name"
                                        name="lastName"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityIcon />
                                                        ) : (
                                                            <VisibilityOffIcon />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <BokunTermAgreements />
                                </Grid>
                            </Grid>

                            {error && (
                                <SnackbarContent
                                    className={classes.error}
                                    message={error && error.message}
                                />
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                color="primary"
                                className={classes.submit}
                                disabled={pristine || loading}
                            >
                                {loading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <React.Fragment>Create Account</React.Fragment>
                                )}
                            </Button>
                            <Grid container justify="center">
                                <Grid item>
                                    <Link
                                        to="/developer/login"
                                        component={RouterLink}
                                        variant="body2"
                                    >
                                        Already have an account? Log in
                                    </Link>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Form>
            </div>
        </Container>
    );
};
